/**
 * The `Issue` record — the hands-on tracker's one type (`docs/dev/admin/02`).
 *
 * It lives **here**, in the admin app, rather than in `@atlas-nook/story`: an issue is not a
 * story-engine primitive, no medium renders one, and nothing a parent ever touches reads it.
 * Putting it in an engine package would have added a record to the product's data model to
 * serve an internal page.
 *
 * `normalizeIssue` is the same defensive reader shape everything else in this app uses: a
 * row that cannot be read is refused rather than half-rendered, and `useQueryDocs` drops and
 * reports it instead of taking the list down.
 *
 * **The id scheme is a counter**, `i-0001`, `i-0002`, …, held in `issues/_meta`. The
 * alternative on the table was a 4-hex suffix, which needs no counter and no transaction —
 * but these ids are *spoken*: Namık pastes "fix i-7 and i-9" into a prompt and the agent
 * types `issues show i-0007` back. `i-3f9a` is not a thing anyone says twice, and it sorts
 * meaninglessly. The counter costs one transaction per create on a collection with a single
 * writer, which is no contention at all.
 */

import { MalformedRecordError } from '@atlas-nook/story';
import type { Timestamp } from 'firebase/firestore';

/** Where it is: the areas `docs/dev/` is divided into, plus the two that cut across them. */
export const ISSUE_AREAS = [
  'studio',
  'admin',
  'patara',
  'engine',
  'devops',
  'design',
  'docs',
] as const;
export type IssueArea = (typeof ISSUE_AREAS)[number];

/** What it is: it's broken · it works but reads wrong · a wish. */
export const ISSUE_KINDS = ['bug', 'rough', 'idea'] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];

/**
 * Where it stands. Namık sets anything; the CLI sets only `fixing` and `fixed`, so
 * `verified` stays a human word said by the person who walked the flow again.
 */
export const ISSUE_STATUSES = ['open', 'fixing', 'fixed', 'verified', 'wontfix'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

/** The two hands on this collection. There is no assignment field; there are two people. */
export type IssueVoice = 'namik' | 'agent';

export interface IssueLogEntry {
  /**
   * A CLIENT timestamp, not `serverTimestamp()`. Firestore refuses a sentinel inside an
   * array element, and the log is an array because it is read whole and never queried.
   */
  at: Timestamp | null;
  by: IssueVoice;
  text: string;
}

export interface Issue {
  id: string;
  title: string;
  note?: string;
  area: IssueArea;
  kind: IssueKind;
  /** The `FLOWS.md` reference — "F4.3", "S8". Free text: a flow can move. */
  step?: string;
  status: IssueStatus;
  /** Storage paths under `issues/{id}/…`, admin-only on both sides. */
  screenshots?: string[];
  /** Status changes and replies, newest last. */
  log: IssueLogEntry[];
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

/**
 * The collection. A root collection, admin-only on both sides of the rules — an issue
 * belongs to the build, not to an author, and nothing under `authors/` may ever reach it.
 */
export const ISSUES = 'issues';

/** The counter document. It sits in the collection it counts, so it is filtered out of reads. */
export const ISSUE_COUNTER_ID = '_meta';

/** `7` → `i-0007`. Four digits: ten thousand findings is more than this product will make. */
export function formatIssueId(count: number): string {
  return `i-${String(count).padStart(4, '0')}`;
}

/** The next counter value, given whatever the `_meta` document currently holds. */
export function nextIssueCount(meta: unknown): number {
  const count = (meta as { count?: unknown } | null | undefined)?.count;
  return typeof count === 'number' && Number.isFinite(count) && count > 0 ? count + 1 : 1;
}

function oneOf<T extends string>(values: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (values as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function optionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function readLog(id: string, value: unknown): IssueLogEntry[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    throw new MalformedRecordError('issue', id, 'log is not an array');
  }
  return value.flatMap((entry): IssueLogEntry[] => {
    const row = entry as { at?: unknown; by?: unknown; text?: unknown };
    const by = oneOf(['namik', 'agent'] as const, row.by);
    if (by === null || typeof row.text !== 'string') return [];
    return [{ at: (row.at as Timestamp | null) ?? null, by, text: row.text }];
  });
}

/**
 * Read one issue, or refuse it.
 *
 * The counter document is the one row in this collection that is not an issue, so it is
 * named rather than guessed at: reads order by `createdAt`, which `_meta` does not carry and
 * which therefore already excludes it, and this is the second lock on the same door.
 */
export function normalizeIssue(id: string, data: unknown): Issue {
  if (id === ISSUE_COUNTER_ID) {
    throw new MalformedRecordError('issue', id, 'the id counter is not an issue');
  }

  const row = (data ?? {}) as Record<string, unknown>;

  const title = typeof row['title'] === 'string' ? row['title'].trim() : '';
  if (title === '') throw new MalformedRecordError('issue', id, 'no title');

  const area = oneOf(ISSUE_AREAS, row['area']);
  if (area === null)
    throw new MalformedRecordError('issue', id, `unknown area ${String(row['area'])}`);

  const kind = oneOf(ISSUE_KINDS, row['kind']);
  if (kind === null)
    throw new MalformedRecordError('issue', id, `unknown kind ${String(row['kind'])}`);

  const status = oneOf(ISSUE_STATUSES, row['status']);
  if (status === null) {
    throw new MalformedRecordError('issue', id, `unknown status ${String(row['status'])}`);
  }

  const screenshots = Array.isArray(row['screenshots'])
    ? row['screenshots'].filter((path): path is string => typeof path === 'string')
    : undefined;

  const note = optionalText(row['note']);
  const step = optionalText(row['step']);

  return {
    id,
    title,
    area,
    kind,
    status,
    log: readLog(id, row['log']),
    createdAt: (row['createdAt'] as Timestamp | null) ?? null,
    updatedAt: (row['updatedAt'] as Timestamp | null) ?? null,
    ...(note === undefined ? {} : { note }),
    ...(step === undefined ? {} : { step }),
    ...(screenshots === undefined || screenshots.length === 0 ? {} : { screenshots }),
  };
}

/** `issues/{id}/{n}.{ext}` — the Storage path an issue's screenshots live at. */
export function issueScreenshotPath(issueId: string, index: number, contentType: string): string {
  const extension =
    contentType === 'image/jpeg' ? 'jpg' : contentType === 'image/webp' ? 'webp' : 'png';
  return `issues/${issueId}/${index}.${extension}`;
}
